import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

function Footer() {
  const { t, i18n } = useTranslation();

  const currentLanguage = i18n.language || "me";
  const isEnglish = currentLanguage.toLowerCase().startsWith("en");
  const currentYear = new Date().getFullYear();

  const logoSrc = isEnglish
    ? "/assets/img/logo/logoENG.png"
    : "/assets/img/logo/logo.png";

  const socialLinks = [
    {
      href: "https://www.facebook.com/fpebar.me",
      icon: "fab fa-facebook-f",
      label: "Facebook",
    },
    {
      href: "https://www.instagram.com/fpepbar/",
      icon: "fab fa-instagram",
      label: "Instagram",
    },
    {
      href: "https://www.youtube.com/@fpepbar",
      icon: "fab fa-youtube",
      label: "YouTube",
    },
    {
      href: "https://www.tiktok.com/@fpepbar",
      icon: "fab fa-tiktok",
      label: "TikTok",
    },
    {
      href: "https://www.linkedin.com/school/fpepbar/",
      icon: "fab fa-linkedin-in",
      label: "LinkedIn",
    },
  ];

  const quickLinks = [
    {
      to: "/upis",
      label: isEnglish ? "Admissions" : "Upis",
    },
    {
      to: "/upisni-rokovi",
      label: isEnglish ? "Admission deadlines" : "Upisni rokovi",
    },
    {
      to: "/svi-studijski-programi",
      label: isEnglish ? "All study programmes" : "Svi studijski programi",
    },
    {
      to: "/cijene-studija",
      label: isEnglish ? "Tuition fees" : "Cijene studija",
    },
    {
      to: "/akreditacija-fpep",
      label: isEnglish ? "Accreditation" : "Akreditacija",
    },
    {
      to: "/o-fpep",
      label: isEnglish ? "About FPEP" : "O FPEP-u",
    },
    {
      to: "/erasmus",
      label: "Erasmus+",
    },
    {
      to: "/projekti",
      label: isEnglish ? "Projects" : "Projekti",
    },
    {
      to: "/novosti",
      label: isEnglish ? "News" : "Novosti",
    },
    {
      to: "/kontakt",
      label: isEnglish ? "Contact" : "Kontakt",
    },

    {
      href: "https://chatbot.fpep.me/",
      label: "Chatbot FPEP",
      external: true,
    },
  ];

  const studyProgramGroups = [
    {
      title: t("footer.osnovneTitle"),
      links: [
        {
          to: "/racunovodstvo-finansije-i-bankarstvo",
          label: isEnglish
            ? "Accounting, Finance and Banking"
            : "Računovodstvo, finansije i bankarstvo",
        },
        {
          to: "/menadzment",
          label: isEnglish ? "Management" : "Menadžment",
        },
        {
          to: "/pravo-osnovne",
          label: isEnglish ? "Law" : "Pravo",
        },
      ],
    },
    {
      title: t("footer.masterTitle"),
      links: [
        {
          to: "/poslovna-ekonomija",
          label: isEnglish ? "Business Economics" : "Poslovna ekonomija",
        },
        {
          to: "/ekonomija-javnog-sektora",
          label: isEnglish
            ? "Public Sector Economics"
            : "Ekonomija javnog sektora",
        },
        {
          to: "/pravo-master",
          label: isEnglish ? "Law" : "Pravo",
        },
      ],
    },
    {
      title: t("footer.specijalistickeTitle"),
      links: [
        {
          to: "/specijalisticke-studije",
          label: isEnglish ? "Important notice" : "Važno obavještenje",
        },
      ],
    },
  ];

  const officialLinks = [
    {
      href: "https://www.gov.me/mps",
      label: isEnglish ? "Ministry of Education" : "Ministarstvo prosvjete",
    },
    {
      href: "https://akokvo.me/",
      label: isEnglish
        ? "Agency for Control and Quality Assurance of Higher Education"
        : "Agencija za kontrolu i obezbjeđenje kvaliteta visokog obrazovanja",
    },
    {
      href: "https://www.univerzitetadriatik.com/pocetna.htm",
      label: isEnglish ? "University Adriatik Bar" : "Univerzitet Adriatik Bar",
    },
    {
      href: "https://eua.eu/",
      label: "European University Association",
    },
  ];

  return (
    <footer
      className="footer-bg footer-p pt-90"
      style={{
        backgroundColor: "#93045b",
        backgroundImage: "url(/assets/img/bg/footer-bg.png)",
      }}
    >
      <div
        className="footer-top pb-70"
        itemScope
        itemType="https://schema.org/CollegeOrUniversity"
      >
        <meta itemProp="name" content="FPEP Bar" />
        <meta itemProp="url" content="https://fpep.me" />

        <div className="container">
          <div className="row justify-content-between">
            <div className="col-xl-4 col-lg-4 col-md-6">
              <div className="footer-widget mb-30">
                <div className="f-widget-title">
                  <h2>{t("footer.aboutTitle")}</h2>
                </div>

                <div className="f-contact textjustify">
                  <p>{t("footer.aboutCompact")}</p>
                </div>

                <div className="footer-social mt-10">
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      title={social.label}
                    >
                      <i className={social.icon} />
                    </a>
                  ))}
                </div>

                <div className="f-widget-title mt-30">
                  <h2>{t("footer.officialLinksTitle")}</h2>
                </div>

                <div className="footer-link">
                  <ul>
                    {officialLinks.map((link) => (
                      <li key={link.href}>
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-xl-2 col-lg-3 col-md-6">
              <div className="footer-widget mb-30">
                <div className="f-widget-title">
                  <h2>{t("footer.quickLinksTitle")}</h2>
                </div>

                <nav
                  className="footer-link"
                  aria-label={t("footer.quickLinksTitle")}
                >
                  <ul>
                    {quickLinks.map((link) => (
                      <li key={link.label}>
                        {link.external ? (
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {link.label}
                          </a>
                        ) : (
                          <Link to={link.to}>{link.label}</Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </div>

            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="footer-widget mb-30">
                <div className="f-widget-title">
                  <h2>{t("footer.studprogramiTitle")}</h2>
                </div>

                <nav
                  className="footer-link"
                  aria-label={t("footer.studprogramiTitle")}
                >
                  {studyProgramGroups.map((group) => (
                    <div key={group.title} className="mb-20">
                      <h6
                        style={{
                          color: "#fbc07d",
                          fontWeight: 700,
                          marginBottom: "12px",
                          fontSize: "16px",
                        }}
                      >
                        {group.title}
                      </h6>

                      <ul>
                        {group.links.map((program) => (
                          <li key={program.to}>
                            <Link to={program.to}>{program.label}</Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </nav>
              </div>
            </div>

            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="footer-widget mb-30">
                <div className="f-widget-title">
                  <h2>{t("footer.kontakt")}</h2>
                </div>

                <div className="f-contact">
                  <ul>
                    <li>
                      <i className="icon fas fa-phone" />
                      <span>
                        <strong>
                          {isEnglish ? "Call us" : "Pozovite nas"}
                        </strong>
                        <br />
                        <a href="tel:+38268003113" itemProp="telephone">
                          +382 (0)68 003-113
                        </a>
                      </span>
                    </li>

                    <li>
                      <i className="icon fas fa-envelope" />
                      <span>
                        <strong>{isEnglish ? "Email us" : "Pišite nam"}</strong>
                        <br />
                        <a href="mailto:fpebar@gmail.com" itemProp="email">
                          fpebar@gmail.com
                        </a>
                      </span>
                    </li>

                    <li>
                      <i className="icon fas fa-map-marker-alt" />
                      <span>
                        <strong>
                          {isEnglish ? "Visit us" : "Posjetite nas"}
                        </strong>
                        <br />
                        <a
                          href="https://www.google.com/maps/search/?api=1&query=16+Rista+Leki%C4%87a,+Bar,+Montenegro"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "inherit" }}
                        >
                          <address
                            style={{ margin: 0, fontStyle: "normal" }}
                            itemProp="address"
                            itemScope
                            itemType="https://schema.org/PostalAddress"
                          >
                            <span itemProp="streetAddress">
                              16 Rista Lekića
                            </span>
                            <br />
                            <span itemProp="addressLocality">Bar</span>,{" "}
                            <span itemProp="addressCountry">
                              {isEnglish ? "Montenegro" : "Crna Gora"}
                            </span>
                          </address>
                        </a>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="copyright-wrap">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-4">
              <div className="copy-text">
                <Link to="/" aria-label="FPEP Bar homepage" itemProp="url">
                  <img src={logoSrc} alt="FPEP Bar logo" itemProp="logo" />
                </Link>
              </div>
            </div>

            <div className="col-lg-4 text-center" />

            <div className="col-lg-4 text-right text-xl-right">
              {isEnglish
                ? `Copyright © FPEP Bar ${currentYear}. All rights reserved.`
                : `Copyright © FPEP Bar ${currentYear}. Sva prava zadržana.`}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
